import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, Signal, ViewChild, WritableSignal, computed, inject, signal, viewChild} from '@angular/core';
import {Report} from '../../classes/report';
import {ReportService} from '../../services/report.service';
import {OngService} from 'frontend/src/app/services/ong.service';
import {RefineryStatus} from '../../classes/refinery-status';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import moment from 'moment';
import * as echarts from 'echarts';
import {ApplicationConfig} from 'frontend/src/app/classes/application-config';
import {FormControl, FormGroup} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CardFilters} from 'frontend/src/shared/classes/card-filters';
import {MatSort} from '@angular/material/sort';
import {EagleiBaseChart} from 'frontend/src/app/classes/charts/base-chart';
import {DataPoint} from 'frontend/src/app/classes/data-point';
import {PopoverElement} from 'frontend/src/app/classes/popover-element';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ModalConfig} from 'frontend/src/app/classes/modal-config';
import {OilRefineryAlertInfoModalComponent} from '../../../widget/modals/oil-refinery-alert-info-modal/oil-refinery-alert-info-modal.component';
import {LayerService} from '../../../layer/services/layer.service';
import {MapOptions} from '../../../map/classes/map-options';
import {LeafletMapLayer} from '../../../layer/classes/leaflet-map-layer';
import {LeafletWmsSource} from '../../../layer/classes/leaflet-wms-source';
import {OngRefinerySource} from '../../../layer/sources/ong-refinery-source';
import { RefineryInfoModalComponent } from '../../modals/refinery-info-modal/refinery-info-modal.component';
import { CustomPopover } from '../../../layer/enum/custom-popover';
import { RefineryInfo } from '../../classes/refinery-info';

interface LineChartData<T = any> {
    points: DataPoint<T>[];
}

@Component({
    selector: 'eaglei-oil-refineries-data-report',
    templateUrl: './oil-refineries-data-report.component.html',
    styleUrls: ['./oil-refineries-data-report.component.scss'],
    standalone: false,
})
export class OilRefineriesDataReportComponent extends Report implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    private chartTarget: Signal<ElementRef> = viewChild('chartTarget');

    // Services
    public reportService = inject(ReportService);
    public ongService = inject(OngService);
    public layerService = inject(LayerService);

    // Filters
    public startDate: moment.Moment = ApplicationConfig.roundMinute().subtract(7, 'days');
    public endDate: moment.Moment = ApplicationConfig.roundMinute();
    public maxDate: moment.Moment = ApplicationConfig.roundMinute();
    public unitFilter: 'all' | 'both' | 'alert' | 'planned' = 'all';
    public processingClassFilter: string = 'all';
    public filterGroup: FormGroup = new FormGroup({
        nameSearch: new FormControl<string>(''),
        unitFilter: new FormControl<string>('all'),
        processingFilter: new FormControl<string>('all')
    });

    // Region Level
    public isRegion: WritableSignal<boolean> = signal(true);
    public selectedRegion: any = undefined;

    // Map Properties
    public mapOptions: MapOptions;
    private mapLayerPadd: LeafletMapLayer;
    public layerSourcePadd: LeafletWmsSource;
    private mapLayerRefinery: LeafletMapLayer;
    public layerSourceRefinery: OngRefinerySource;

    // Refineries Level
    public selectedRefinery = undefined;

    // Chart Properties
    public baseChart = new EagleiBaseChart<any, any, any>();
    private readonly chartColor = [
        {offset: 0, color: 'rgba(2, 136, 243, 0.25)'},
        {offset: 1, color: 'rgba(255, 255, 255)'},
    ];
    public chartData: any[] = [];

    // Table Properties
    private readonly regionColumns: string[] = [
        'padd',
        'lastYearChange',
        'capacityChange',
        'impacted',
        'totalCapacity',
        'alerts',
        'plannedOutages',
        'actions',
    ];
    private readonly refineryColumns: string[] = [
        'name',
        'totalUnits',
        'online',
        'offline',
        'impactedRef',
        'totalCapacityRef',
        'alerts',
        'plannedOutages',
    ];
    public columnNames: Signal<string[]> = computed(() => {
        return (this.isRegion()) ? [...this.regionColumns] : [...this.refineryColumns];
    });

    constructor(private dialog: MatDialog, private cd: ChangeDetectorRef) {
        super();

        this.filterGroup
            .get('nameSearch')
            .valueChanges.pipe(takeUntilDestroyed())
            .subscribe((val: string) => {
                this.dataSource.filter = val.trim().length > 0 ? val : ' ';
            });

        this.filterGroup
            .get('unitFilter')
            .valueChanges.pipe(takeUntilDestroyed())
            .subscribe((val: 'all' | 'both' | 'alert' | 'planned') => {
                this.unitFilter = val;
                this.updateUnitFilter()
            });

        this.filterGroup
            .get('processingFilter')
            .valueChanges.pipe(takeUntilDestroyed())
            .subscribe((val: string) => {
                this.processingClassFilter = val;
                this.updateProcessingClassFilter(val);
            });

        this.mapOptions = new MapOptions().setZoom(4, 1).setCenter(37.2, -92);
        this.mapOptions.onlyManualZoom = true;
        this.mapOptions.show = {
            sidebar: false,
            refresh: false,
            export: false,
            coverage: false,
            coordinate: false,
            zoom: true,
            panControl: true,
        };
    }

    public ngOnInit(): void {
        this.reportService.getReportData().subscribe((r) => this.initializeReportInfo(r));

        this.getMapLayers();
    }

    public ngAfterViewInit(): void {
        this.getPaddRegionStatus();

        this.initChart();
    }

    public getPaddRegionStatus(): void {
        this.ongService.getRefineryDataReport(this.startDate, this.endDate, this.processingClassFilter, true).subscribe((res) => {
            // TODO: Get this done on the backend, only here for the visiual
            this.initData(res.map(d => {
                if (d.padd_region === 'PADD 1B') {
                    d.padd_region = 'PADD 1';
                }

                return d;
            }));
        });

        this.getMapLayers();
    }

    public getRefineryStatus(): void {
        this.initChart();

        this.ongService.getRefineryDataReport(this.startDate, this.endDate, this.processingClassFilter, false).subscribe((res) => {
            this.initData(res.filter(r => r.padd_region === this.selectedRegion.padd_region).map(filtered => new RefineryInfo(filtered)));
        });
    }

    public getMapLayers(): void {
        this.layerService.getLayerByHandle('padd').subscribe((layer) => {
            this.mapLayerPadd = layer;
            this.layerSourcePadd = new LeafletWmsSource(layer);

            this.layerSourcePadd.fetchImages();
            this.layerSourcePadd.addToMap();
        });

        this.layerService.getLayerByHandle('ongRefineries').subscribe((layer) => {
            this.mapLayerRefinery = layer;
            this.layerSourceRefinery = new OngRefinerySource(layer);

            this.mapLayerRefinery.customPopoverId = CustomPopover.ONG_REFINERIES;

            this.ongService.getRefineryStatus().subscribe((ref) => {
                this.ongService.getRefineryDataReport(this.startDate, this.endDate, this.processingClassFilter, false).subscribe((res) => {
                    const updatedRefData = res.map(filtered => new RefineryInfo(filtered));

                    ref.features.forEach(f => {
                        const foundRef = updatedRefData.find(u => u.facility_id === f.properties.facility_id)
                        if (foundRef !== undefined) {
                            f.properties = foundRef;
                        }
                    });

                    this.layerSourceRefinery.processFeatures(ref.features);
                    
                    this.layerSourceRefinery.addToMap();
                });
            });
        });
    }

    private initData(data: any[]): void {
        if (this.dataSource) {
            this.dataSource.data = data;
        } else {
            this.dataSource = new MatTableDataSource(data);
            this.dataSource.sortingDataAccessor = this.dataAccessor.bind(this);
            this.dataSource.filterPredicate = this.filterPredicate.bind(this);
        }
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        this.dataSource.filter = ' ';
    }

    private dataAccessor(data: any, column: string): string | number {
        switch (column) {
            case 'name':
                return data.name.toLowerCase();
            case 'padd':
                return data.region.toLowerCase();
            case 'lastYearChange':
                return data.pcnt_change_in_capacity_minus_1_yr;
            case 'capacityChange':
                return data.pcnt_change_in_capacity;
            case 'impacted':
                return data.impacted_capacity_at_end_date;
            case 'totalCapacity':
                return data.capacity_at_end_date;
            case 'impactedRef':
                return data.offline_capacity;
            case 'totalCapacityRef':
                return data.total_capacity;
            case 'alerts':
                return data.refinery_alert_count;
            case 'plannedOutages':
                return data.planned_outage_count;
            case 'totalUnits':
                return data.total_units;
            case 'online':
                return data.online_units;
            case 'offline':
                return data.offline_units;
            default:
                return '';
        }
    }

    private filterPredicate(data: RefineryStatus, searchText: string): boolean {
        return (this.isRegion()) ? true : data.name?.toLowerCase().includes(searchText.toLowerCase());
    }

    public updateDateRange(dates: CardFilters): void {
        this.startDate = dates.startDate.clone();
        this.endDate = dates.endDate.clone();

        if (this.isRegion()) {
            this.getPaddRegionStatus();
        } else {
            this.getRefineryStatus();
        }

        if (!!this.selectedRefinery) {
            this.toggleSelectedRow(this.selectedRefinery);
        }
    }

    public updateUnitFilter(): void {
        this.renderChart();
    }

    public updateProcessingClassFilter(value: string): void {
        this.processingClassFilter = value;

        if (this.isRegion()) {
            this.getPaddRegionStatus();
        } else {
            this.getRefineryStatus();
        }
    }

    public viewRegion(region: any): void {
        this.selectedRegion = region;
        this.isRegion.set(false);

        this.cd.detectChanges();

        this.getRefineryStatus();
    }

    public returnToRegions(): void {
        this.isRegion.set(true);

        this.selectedRefinery = undefined;
        this.selectedRegion = undefined;
        this.filterGroup.get('nameSearch').setValue(' ');

        this.getPaddRegionStatus();
    }

    public openInfoModal(event: any, type: 'alert' | 'planned', rowInfo: any): void {
        event.stopPropagation();

        let padd = rowInfo.padd_region;
        if (!this.isRegion()) {
            padd = this.selectedRegion.padd_region;
        }

        this.ongService.getOilAlertPlannedInfo(this.startDate, this.endDate, (type === 'planned'), padd).subscribe((res) => {
            let dataTable = res;

            if (!this.isRegion()) {
                dataTable = res.filter((d) => {
                    // TODO: Sync backend better to remove if
                    if (type === 'planned') {
                        return (d as any).name === rowInfo.name;
                    } else {
                        return d.facility_id === rowInfo.facility_id;
                    }
                });
            }

            const config: MatDialogConfig = {
                width: ModalConfig.getModalWidth(),
                disableClose: true,
                autoFocus: false,
                data: {
                    regionInfo: rowInfo,
                    refineryInfo: rowInfo,
                    dataTable,
                    isPlanned: type === 'planned',
                },
            };
            this.dialog.open(RefineryInfoModalComponent, config);
        });
    }

    public toggleSelectedRow(row: RefineryStatus): void {
        if (this.isRegion()) {
            return;
        }
        this.selectedRefinery = row;

        this.ongService.getRefineryDetails(this.startDate, this.endDate, row.facility_id).subscribe((res) => {
            this.chartData = [...res];

            this.renderChart();
        });
    }

    private initChart(): void {
        this.baseChart.interactivePopover = true;
        this.baseChart.initializeEChart(this.chartTarget().nativeElement, true, 'Date', 'Capacity');

        this.baseChart.getXAxis().type = 'time';
        this.baseChart.getXAxis().boundaryGap = false;

        this.baseChart.getXAxis().axisLabel.formatter = (label) => {
            return moment(label).format('M/D');
        };

        this.baseChart.getYAxis().nameTextStyle = {
            padding: [0, 0, 0, 0],
        };
    }

    private renderChart(): void {
        const series: any[] = [];
        const legend: string[] = [];
        const lStats: any[] = [];
        let legendStatus = {};
        this.chartData.forEach((data) => {
            const details = data.daily_agg;
            const alertDetails = data.alert_agg;
            const plannedDetails = data.planned_outage_agg;
            const line: LineChartData = {
                points: [],
            };

            const getPopoverData = (popData: any, alertData: any, plannedData: any) => {
                const modalOpen = (apData, planned) => {
                    const config: MatDialogConfig = {
                        width: ModalConfig.getModalWidth(),
                        disableClose: true,
                        autoFocus: false,
                        data: {
                            event: apData,
                            isPlanned: planned,
                        },
                    };
                    this.dialog.open(OilRefineryAlertInfoModalComponent, config);
                };

                const data = [
                    new PopoverElement('Title', `${popData.category} - ${popData.unit}`),
                    new PopoverElement('Capacity', popData.unit_capacity || 0),
                    new PopoverElement('Measurement Date', moment(popData.measurement_date).format('M/D/YY')),
                ];

                if (!!alertData) {
                    data.push(new PopoverElement('Alert', alertData, false, true).setOnClick(() => modalOpen(alertData, false)));
                }

                if (!!plannedData) {
                    data.push(
                        new PopoverElement('Planned Outage', plannedData, false, true).setOnClick(() => modalOpen(plannedData, true))
                    );
                }

                return data;
            };

            line.points = details
                ?.sort((a, b) => (moment(a.measurement_date).isBefore(moment(b.measurement_date)) ? 1 : -1))
                .map((d) => {
                    const val = d.unit_capacity || 0;

                    const alert = alertDetails?.find(
                        (a) => moment(a.distributed_datetime).startOf('day').valueOf() === moment(d.measurement_date).valueOf()
                    );
                    const planned = plannedDetails?.find((p) =>
                        moment(d.measurement_date).isBetween(moment(p.observed_start_date), moment(p.observed_end_date), 'day', '[]')
                    );

                    return new DataPoint({
                        x: moment(d.measurement_date).valueOf(),
                        y: val,
                        popoverData: getPopoverData(d, alert, planned),
                        color: '#0288F3',
                        details: data,
                    });
                });

            this.baseChart.eChartOptions.xAxis['data'] = line.points?.map((dp) => moment(dp.data.x).valueOf());

            const lineData = line.points?.map((point) => {
                return {
                    value: [point.data.x, point.data.y],
                    popoverData: point.data.popoverData,
                    symbol: this.getSymbolType(point.data),
                };
            });

            const unitName = `${data.unit}`;
            series.push({
                name: unitName,
                type: 'line',
                smooth: 0.5,
                symbolSize: 8,
                data: lineData,
            });

            legend.push(data.unit);
            lStats.push({[unitName]: this.getSelectStatus(alertDetails, plannedDetails)});
        });
        legendStatus = Object.assign({}, ...lStats);

        this.baseChart.eChartOptions.legend = {data: legend, type: 'scroll', left: '5%', right: '2%', selected: legendStatus};
        this.baseChart.eChartOptions.series = series;
        this.baseChart.eChart.setOption(this.baseChart.eChartOptions, true);
    }

    private getSymbolType(data: any): string {
        const alert = data.details.alert_agg?.find((d) => moment(d.distributed_datetime).startOf('day').valueOf() === data.x);
        const planned = data.details.planned_outage_agg?.find((d) =>
            moment(data.x).isBetween(moment(d.observed_start_date), moment(d.observed_end_date), 'day', '[]')
        );

        if (!!alert && !!planned) {
            return 'emptyDiamond';
        } else if (!!alert && !planned) {
            return 'emptyTriangle';
        } else if (!alert && !!planned) {
            return 'emptyRect';
        }

        return 'emptyCircle';
    }

    private getSelectStatus(alerts: any, planned: any): boolean {
        switch (this.unitFilter) {
            case 'all':
                return true;
            case 'both':
                return alerts?.length > 0 && planned?.length > 0;
            case 'alert':
                return alerts?.length > 0;
            case 'planned':
                return alerts?.length > 0;
            default:
                return true;
        }
    }

    public getRegionColor(region: any): string {
        let color = '';
        switch (region.padd_region) {
            case 'PADD 1B':
                color = '#7b3294';
                break;
            case 'PADD 2':
                color = '#ff7f00';
                break;
            case 'PADD 3':
                color = '#41b6c4';
                break;
            case 'PADD 4':
                color = '#2c7fb8';
                break;
            case 'PADD 5':
                color = '#253494';
                break;
            default:
                color = '#7b3294';
                break;
        }

        const style = `border: solid 2px ${color}; background: repeating-linear-gradient(to right, ${color}, ${color} 1px, transparent 1px, transparent 2.5px)`;
        return style;
    }

    public exportTable(): void {
        console.error('Not Implmented');
    }
}
